import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Simple trick that lets you code twice as fast",
  "description": "",
  "published": "2012-10-08T00:00:00.000Z",
  "redirect_from": ["/blog/simple-trick-that-lets-you-code-twice-as-fast/swizec/5382"],
  "categories": "Automation, Programming, Quality assurance, Test automation, Test suite, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://icant.co.uk/talks/h5/pictures/smashingconf/shorterjourney.jpg",
        "alt": "Basic idea from THE Bret Victor talk",
        "title": "Basic idea from THE Bret Victor talk"
      }}></img></p>
    <p>{`Immediate feedback.`}</p>
    <p>{`No really, it's that simple.`}</p>
    <p>{`Most of our a developer's revolves around verifying code. After `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/ive-hit-a-glass-ceiling-of-coding-productivity-now-what/swizec/5191",
        "title": "I’ve hit a glass ceiling of coding productivity. Now what?"
      }}>{`hitting the limit of how fast you can type`}</a>{`, the only way to think faster is getting feedback instantly.`}</p>
    <p>{`There's actually two parts to this trick.`}</p>
    <h2 {...{
      "id": "1-automated-testing"
    }}>{`1. Automated testing`}</h2>
    <p>{`First of all, you should write tests. No need for full on TDD writing tests before writing code, just have tests, `}<em parentName="p">{`any`}</em>{` tests. Because eventually you will want to know if this thing you've just coded works.`}</p>
    <p>{`Your first test is: does it run?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/10/mocha-testing.png",
        "alt": "Mocha automated automated testing",
        "title": "Mocha automated automated testing"
      }}></img></p>
    <p>{`The next test is: does it do what it's supposed to?`}</p>
    <p>{`Without an automated `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Test_suite",
        "title": "Test suite"
      }}>{`test suite`}</a>{` this one is tricky. It takes `}<em parentName="p">{`ages`}</em>{`. `}{`_`}{`Decades `}{`_`}{`compared to how easy it was to write. At worst, you have to check your whole program after every change. Good design alleviates a lot of this burden due to isolation, but still ... effort.`}</p>
    <p>{`I still remember when every change meant some 20 minutes of clicking around to check if things worked. And I only remembered to click around the thing I just changed, never you mind any ripple effects in the codebase.`}</p>
    <p>{`Horror.`}</p>
    <p>{`Automated testing reduces those 20 minutes to, say, 30 seconds.`}</p>
    <p>{`Your life becomes:`}</p>
    <ol>
      <li parentName="ol">{`Write some code`}</li>
      <li parentName="ol">{`Hop into a console of some sort`}</li>
      <li parentName="ol">{`Possibly compile things`}</li>
      <li parentName="ol">{`Run the test suite`}</li>
      <li parentName="ol">{`Wait while it runs`}</li>
      <li parentName="ol">{`Give in to the urge to quickly check twitterredditfacebooksomething`}</li>
      <li parentName="ol">{`Go back to coding long after the tests finished`}</li>
    </ol>
    <p>{`Great, it's just taken a minute (not counting twitterredditfacebooksomething time) to find out if something works. I do this every ~5 minutes, that means a waste of 10 minutes per every hour worked.`}</p>
    <h2 {...{
      "id": "2-automated-automated-testing"
    }}>{`2. Automated automated testing`}</h2>
    <p>{`One hour and twenty minutes every full workday.`}</p>
    <p>{`Man, that's some non-negligible time right there!`}</p>
    <p>{`Then again, I've got an automated test suite, why am I running it manually? Wouldn't it be better if something ran the test suite every time I hit `}<em parentName="p">{`Ctrl+S`}</em>{`?`}</p>
    <p>{`And that's exactly how it's supposed to be!`}</p>
    <p>{`Let `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Test_automation",
        "title": "Test automation"
      }}>{`automated testing`}</a>{` run automatically and just glance at the results every so often. You won't break concentration, you won't be tempted to check twitter while the tests run, you won't have to get out of your `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Text_editor",
        "title": "Text editor"
      }}>{`text editor`}</a>{`.`}</p>
    <p>{`Win-win-win.`}</p>
    <p>{`Right now I use two tools to do this (and am looking for more)`}</p>
    <p><a parentName="p" {...{
        "href": "http://visionmedia.github.com/mocha/"
      }}>{`Mocha`}</a>{`'s got a wonderful `}<em parentName="p">{`-w`}</em>{` option for running node.js tests - it watches files for changes and reruns the suite every time you save. And Mocha is just generally wonderful, but that's a different post.`}</p>
    <p>{`For frontend development, I'm using `}{`_`}<a parentName="p" {...{
        "href": "https://chrome.google.com/webstore/detail/live-css-mod/pcfngkdgaccedbamgamojgomaibafdcg"
      }}>{`Live CSS Mod`}</a>{` - `}{`_`}{`a Chrome extension, which refreshes a page every couple of seconds. But it's smart enough to do this in the background and only show the changes if there's anything different. This means you don't lose state all the time because that would be annoying.`}</p>
    <p>{`Now all I need are these sorts of tools for the other stacks I'm using. (Django/python and Haskell).`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/errors-part-learning-curve"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_124_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/errors-part-learning-curve"
        }}>{`Errors: part of the learning curve`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.modernperlbooks.com/mt/2012/08/why-i-use-perl-testing.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_2_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.modernperlbooks.com/mt/2012/08/why-i-use-perl-testing.html"
        }}>{`Why I Use Perl: Testing`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.smartbear.com/software-quality/bid/201324/Divide-Your-Automated-Testing-Efforts"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/115656693_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.smartbear.com/software-quality/bid/201324/Divide-Your-Automated-Testing-Efforts"
        }}>{`Divide Your Automated Testing Efforts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://corfield.org/blog/post.cfm/automated-browser-based-testing-with-clojure"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_71_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://corfield.org/blog/post.cfm/automated-browser-based-testing-with-clojure"
        }}>{`Automated Browser-based Testing with Clojure`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.techwench.com/how-to-perform-software-automation-testing-step-by-step-tutorial/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_113_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.techwench.com/how-to-perform-software-automation-testing-step-by-step-tutorial/"
        }}>{`How to Perform Software Automation Testing - Step by Step Tutorial`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=c3e2471f-2e38-487a-a839-306d9e4f87b9",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      